import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import { ThemeProvider, useTheme } from '@makyoapp/makyo-ui'

import { OrgsProvider } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'

import AppRoutes from './AppRoutes'
import { AppProvider } from './context/AppContext'
import { CacheProvider } from './context/CacheContext'
import { UploadProvider } from './context/UploadContext'
import { LIGHT_ONLY_ROUTE, STORAGE_KEYS } from './shared/constants'
import { getLanguageDirection } from './shared/lang'

import './i18n/config'

function App() {
  const location = useLocation()
  const { i18n } = useTranslation()
  const { state: themeState } = useTheme()
  const { userData, setLang, logout } = useAuth()

  /**
   * handle invalid token
   */
  useEffect(() => {
    const handleInvalidToken = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.USER_SESSION && e.newValue === null) {
        logout()
      }
    }
    window.addEventListener('storage', handleInvalidToken)
    return function cleanup() {
      window.removeEventListener('storage', handleInvalidToken)
    }
  }, [logout])

  /**
   * only once when language change
   * and page is new request from a new tab / browser page
   * the rest is handled by `AuthContext`
   */
  useEffect(() => {
    if (userData) {
      const savedLang = userData.lang
      setLang(savedLang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * dark theme mode
   */
  const isDarkMode = useMemo(() => {
    return (
      (themeState.mode === 'dark' && !LIGHT_ONLY_ROUTE.includes(location.pathname)) ||
      (themeState.mode === ('system' as any) && window.matchMedia('(prefers-color-scheme: dark)').matches)
    )
  }, [themeState.mode, location.pathname])

  /**
   * handle theme mode and language direction
   */
  useEffect(() => {
    document.body.dir = getLanguageDirection(userData?.lang)
    document.documentElement.classList.toggle('dark', isDarkMode)
    document.documentElement.setAttribute('data-color-mode', isDarkMode ? 'dark' : 'light')
  }, [i18n, userData?.lang, location.pathname, themeState.mode, isDarkMode])

  return (
    <AppProvider>
      <UploadProvider>
        <OrgsProvider>
          <div id='app' className='text-m-m'>
            <AppRoutes />
            <ToastContainer containerId='for-all' theme={isDarkMode ? 'light' : 'dark'} position='bottom-right' />
            <ToastContainer containerId='for-sw' theme={isDarkMode ? 'light' : 'dark'} position='bottom-right' />
          </div>
        </OrgsProvider>
      </UploadProvider>
    </AppProvider>
  )
}

export default function AppWrapper() {
  const { userData } = useAuth()
  const { i18n } = useTranslation()

  const languageDir = useMemo(() => {
    return userData ? i18n.dir(userData.lang) : undefined
  }, [userData, i18n])

  return (
    <CacheProvider>
      <ThemeProvider dir={languageDir}>
        <App />
      </ThemeProvider>
    </CacheProvider>
  )
}
